// @core/stores/billing/BillingBillingPolicyStore.ts
// Auto-generated by corebuilder. DO NOT EDIT.

import { makeAutoObservable, runInAction } from 'mobx';
import { billingApi } from '@core/api';
import type {
  BillingPolicy,
  CreateBillingPolicyRequest,
  ResolveBillingContextRequest,
  ResolveBillingContextResponse,
  UpdateBillingPolicyRequest,
} from '@core/types/billing';
import type { PaginatedRequest, PageInfo } from '@core/types/pagination';

class BillingBillingPolicyStore {
  // List state
  items: BillingPolicy[] = [];
  pageInfo: PageInfo | null = null;

  // Single item state
  currentItem: BillingPolicy | null = null;

  // Loading states
  isLoading = false;
  isLoadingItem = false;
  isSaving = false;
  isDeleting = false;

  // Error state (stores full ApiClientError for request context)
  error: unknown = null;
  // Cache by ID
  private cache = new Map<string, BillingPolicy>();

  constructor() {
    makeAutoObservable(this);
  }
  // Computed
  get hasMore(): boolean {
    return this.pageInfo?.has_next ?? false;
  }

  get isEmpty(): boolean {
    return this.items.length === 0 && !this.isLoading;
  }

  // Cache helpers
  getById(id: string): BillingPolicy | undefined {
    return this.cache.get(id);
  }
  private updateCache(item: BillingPolicy | undefined): void {
    if (item?.entity?.id) {
      this.cache.set(item.entity.id, item);
    }
  }
  private removeFromCache(id: string): void {
    this.cache.delete(id);
  }

  // Actions
  setError(error: unknown): void {
    this.error = error;
  }

  clearError(): void {
    this.error = null;
  }
  clearCurrent(): void {
    this.currentItem = null;
  }

  reset(): void {
    this.items = [];
    this.pageInfo = null;
    this.currentItem = null;
    this.error = null;
    this.cache.clear();
  }

  /**
   * Create billing policy
   */
  async CreateBillingPolicy(data: CreateBillingPolicyRequest): Promise<BillingPolicy> {
    this.isSaving = true;
    this.error = null;

    try {
      const response = await billingApi.CreateBillingPolicy(data);
      const item = response.policy;

      runInAction(() => {
        if (item) {
          this.items = [item, ...this.items];
          this.updateCache(item);
        }
        this.isSaving = false;
      });

      return item!;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isSaving = false;
      });
      throw error;
    }
  }

  /**
   * Delete billing policy
   */
  async DeleteBillingPolicy(policy_id: string): Promise<void> {
    this.isDeleting = true;
    this.error = null;

    try {
      await billingApi.DeleteBillingPolicy(policy_id);

      runInAction(() => {
        // Remove from list
        this.items = this.items.filter(i => i.entity?.id !== policy_id);

        // Clear current item if same
        if (this.currentItem?.entity?.id === policy_id) {
          this.currentItem = null;
        }

        this.removeFromCache(policy_id);
        this.isDeleting = false;
      });
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isDeleting = false;
      });
      throw error;
    }
  }

  /**
   * Get billing policy
   */
  async GetBillingPolicy(policy_id: string): Promise<BillingPolicy> {
    // Check cache first
    const cached = this.cache.get(policy_id);
    if (cached) {
      this.currentItem = cached;
      return cached;
    }

    this.isLoadingItem = true;
    this.error = null;

    try {
      const response = await billingApi.GetBillingPolicy(policy_id);
      const item = response.policy;

      runInAction(() => {
        this.currentItem = item ?? null;
        this.updateCache(item);
        this.isLoadingItem = false;
      });

      return item!;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoadingItem = false;
      });
      throw error;
    }
  }

  /**
   * Get entity billing policy
   */
  async GetBillingPolicyByEntity(entity_type: string, entity_id: string): Promise<BillingPolicy> {
    // Check cache first
    const cached = this.cache.get(entity_id);
    if (cached) {
      this.currentItem = cached;
      return cached;
    }

    this.isLoadingItem = true;
    this.error = null;

    try {
      const response = await billingApi.GetBillingPolicyByEntity(entity_type, entity_id);
      const item = response.policy;

      runInAction(() => {
        this.currentItem = item ?? null;
        this.updateCache(item);
        this.isLoadingItem = false;
      });

      return item!;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoadingItem = false;
      });
      throw error;
    }
  }

  /**
   * List billing policies
   */
  async ListBillingPolicies(params?: PaginatedRequest): Promise<void> {
    this.isLoading = true;
    this.error = null;

    try {
      const response = await billingApi.ListBillingPolicies(params);

      runInAction(() => {
        this.items = response.policies?.policies ?? [];
        this.pageInfo = response.pagination?.page_info ?? null;
        this.isLoading = false;

        // Update cache
        this.items.forEach(item => this.updateCache(item));
      });
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Resolve billing context
   */
  async ResolveBillingContext(data: ResolveBillingContextRequest): Promise<ResolveBillingContextResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await billingApi.ResolveBillingContext(data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Update billing policy
   */
  async UpdateBillingPolicy(policy_id: string, data: UpdateBillingPolicyRequest): Promise<BillingPolicy> {
    this.isSaving = true;
    this.error = null;

    try {
      const response = await billingApi.UpdateBillingPolicy(policy_id, data);
      const item = response.policy;

      runInAction(() => {
        if (item) {
          // Update in list
          const index = this.items.findIndex(i => i.entity?.id === item.entity?.id);
          if (index !== -1) {
            this.items[index] = item;
          }

          // Update current item if same
          if (this.currentItem?.entity?.id === item.entity?.id) {
            this.currentItem = item;
          }

          this.updateCache(item);
        }
        this.isSaving = false;
      });

      return item!;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isSaving = false;
      });
      throw error;
    }
  }
}

export const billingBillingPolicyStore = new BillingBillingPolicyStore();
